import React from 'react';
import {
  Card, CircularGaugeChart, ellipsis, FontWeight, Helper, HStack, Icon, ICON_INFORMATION_OUTLINE, Message, ModalSize, Portal, Spacing, Stack, Text,
  useModal,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import Markdown from 'react-markdown';
import { GoalMetric, IndividualGoal, TimeRange } from '../../../providers/api/dtos';
import { configByMetric, configByPerformanceRating } from './constants';
import { template } from '../../../utils/text/text';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import GoalPerformanceDetailView from '../goal-performance-detail-view/GoalPerformanceDetailView';

export interface GoalPerformanceSummaryCardProps extends React.AriaAttributes {
  metric: GoalMetric,
  goal: IndividualGoal,
}

/**
 * @todo rename to `IndividualGoalPerformanceSummaryCard`
 */
const GoalPerformanceSummaryCard: React.FC<GoalPerformanceSummaryCardProps> = ({
  metric,
  goal,
  ...rest
}) => {
  const timeRange: TimeRange = ['rumors', 'prospects'].includes(metric) ? 'weekly' : 'monthly';
  const { yearly } = goal.metrics[metric];
  const performanceDetailsModal = useModal();

  const cfgByMetric = configByMetric[metric];
  const cfgByPerformanceRating = configByPerformanceRating(goal.metrics.sales.yearly.progress >= 1)[yearly.status];

  const formattedYearlyGoal = Math.round(yearly.goal);

  return (
    <>
      <Card {...rest}>
        <Card.Content>
          <Spacing padding={[50, 0]}>
            <HStack
              gap={100}
              alignItems="start"
              justifyContent="space-between"
              wrap="nowrap"
            >
              <HStack
                gap={300}
                alignItems="start"
                wrap="nowrap"
              >
                <div style={{ width: '6.25rem' }}>
                  <Stack
                    gap={150}
                    alignItems="center"
                  >

                    <CircularGaugeChart
                      progress={yearly.progress}
                      color={cfgByPerformanceRating.messageType}
                      size="L"
                    />

                    <Text.Caption
                      color={`${cfgByPerformanceRating.messageType}.default.low`}
                      css={ellipsis()}
                    >
                      {cfgByMetric.chartCaption}
                    </Text.Caption>
                  </Stack>
                </div>

                <Spacing padding={[75, 0]}>
                  <Stack gap={150}>
                    <Text.H4 fontWeight={FontWeight.LIGHT}>
                      <Markdown components={{ p: ({ _node, ...props }) => props.children }}>
                        {template(cfgByMetric.titleMarkdown, {
                          PERFORMANCE: yearly.performance,
                          GOAL: formattedYearlyGoal,
                        })}
                      </Markdown>
                    </Text.H4>

                    <Message
                      icon={cfgByPerformanceRating.icon}
                      message={cfgByPerformanceRating.message}
                      type={cfgByPerformanceRating.messageType}
                      onClick={performanceDetailsModal.open}
                    />
                  </Stack>
                </Spacing>
              </HStack>

              <Helper
                content={template(cfgByMetric.helperContent, {
                  AVG_COMMISSIONS: formatEuro(goal.averageCommission),
                })}
              >
                <Icon
                  path={ICON_INFORMATION_OUTLINE}
                  size={24}
                />
              </Helper>
            </HStack>
          </Spacing>
        </Card.Content>
      </Card>
      <Portal>
        <SimpleModal
          {...performanceDetailsModal}
          aria-label="Informazioni dettagliate performance obiettivo"
          title={template(cfgByPerformanceRating.modalTitle, {
            METRIC: cfgByMetric.translation,
          })}
          size={ModalSize.MEDIUM}
        >
          <GoalPerformanceDetailView
            metric={metric}
            goal={goal}
            timeRange={timeRange}
            onNewGoalAccepted={() => performanceDetailsModal.close()}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default GoalPerformanceSummaryCard;
